import type { StepProps } from '@/components/CreateCampaignDialog/createCampaignType'
import buttonStyles from '@/styles/buttons.module.css'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import styles from './CampaignAssetsStep.module.css'

interface UploadedFile {
  id?: number
  name: string
  size: number
  date: string
  type: string
  file: File
}

// Create a stable, order-independent signature of files
const computeFilesSignature = (fileList: File[]): string => {
  if (!fileList || fileList.length === 0) return 'EMPTY'
  const parts = fileList.map((f) => `${f.name}|${f.size}|${f.lastModified ?? ''}|${f.type}`).sort()
  return `${fileList.length}::${parts.join('||')}`
}

export const CampaignAssetsStep = ({ data, updateData }: StepProps) => {
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const updateParentWithFiles = (updatedFiles: UploadedFile[]) => {
    const assetFiles = updatedFiles.map((f) => f.file)
    const assetFilesWithIds = updatedFiles.map((f) => ({ file: f.file, id: f.id }))
    const sig = computeFilesSignature(assetFiles)
    updateData({
      assetFiles,
      assetFilesWithIds,
      assetFilesSignature: sig,
      assetFilesChanged: data.uploadedAssetsSignature ? data.uploadedAssetsSignature !== sig : true,
    })
  }

  useEffect(() => {
    if (data.assetFiles && data.assetFiles.length > 0 && files.length === 0) {
      const assetFilesWithIds = data.assetFilesWithIds as Array<{ file: File; id?: number }> | undefined

      const loadedFiles: UploadedFile[] = data.assetFiles.map((file) => {
        const storedFile = assetFilesWithIds?.find((stored) => stored.file.name === file.name && stored.file.size === file.size)

        return {
          id: storedFile?.id, // Will be undefined if not uploaded yet
          name: file.name,
          size: file.size,
          date: formatDate(new Date()),
          type: file.type,
          file: file,
        }
      })
      setFiles(loadedFiles)

      const sig = computeFilesSignature(data.assetFiles)
      if (data.assetFilesSignature !== sig) {
        updateData({
          assetFilesSignature: sig,
          assetFilesChanged: data.uploadedAssetsSignature ? data.uploadedAssetsSignature !== sig : true,
        })
      }
    }
  }, [data.assetFiles, data.assetFilesWithIds, data.assetFilesSignature, data.uploadedAssetsSignature, files.length, updateData])

  // Sync backend IDs with local state after upload
  useEffect(() => {
    const assetFilesWithIds = data.assetFilesWithIds as Array<{ file: File; id?: number }> | undefined

    if (assetFilesWithIds && files.length > 0) {
      let hasChanges = false
      const updatedFiles = files.map((file) => {
        const fileWithBackendId = assetFilesWithIds.find((f) => f.file.name === file.name && f.file.size === file.size)
        if (fileWithBackendId?.id && fileWithBackendId.id !== file.id) {
          hasChanges = true
          return { ...file, id: fileWithBackendId.id }
        }
        return file
      })

      if (hasChanges) setFiles(updatedFiles)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.assetFilesWithIds, files])

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${Math.round(bytes / k ** i)} ${sizes[i]}`
  }

  const formatDate = (date: Date): string => {
    const day = date.getDate()
    const month = date.toLocaleString('en-US', { month: 'short' })
    const year = date.getFullYear()
    return `${day} ${month} ${year}`
  }

  const getFileIcon = (fileType: string): string => {
    if (fileType.includes('pdf')) return 'PDF'
    if (fileType.includes('word') || fileType.includes('document')) return 'DOC'
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'XLS'
    if (fileType.includes('image')) return 'IMG'
    return 'FILE'
  }

  const getFileIconColor = (fileType: string): string => {
    if (fileType.includes('pdf')) return '#E74C3C'
    if (fileType.includes('word') || fileType.includes('document')) return '#2E86DE'
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '#27AE60'
    if (fileType.includes('image')) return '#9B59B6'
    return '#95A5A6'
  }

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList) return

    const newFiles: UploadedFile[] = Array.from(fileList).map((file) => ({
      name: file.name,
      size: file.size,
      date: formatDate(new Date()),
      type: file.type,
      file: file,
    }))

    const updatedFiles = [...files, ...newFiles]
    setFiles(updatedFiles)

    updateParentWithFiles(updatedFiles)
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  const handleRemoveFile = async (fileName: string) => {
    const fileToRemove = files.find((f) => f.name === fileName)
    if (!fileToRemove) return

    // If file has backend id, delete it via API first
    if (fileToRemove.id) {
      try {
        const { default: api } = await import('@/services/api')
        const { toast } = await import('react-toastify')

        await api.delete('/brands/assets', {
          data: {
            ids: [fileToRemove.id],
          },
        })

        toast.success('File deleted successfully')
      } catch (error) {
        console.error('Failed to delete asset from backend:', error)
        const { toast } = await import('react-toastify')
        toast.error('Failed to delete file from server')
        return // Don't remove from local state if backend deletion failed
      }
    } else {
      console.error('File has no ID')
    }

    // Remove from local state only after successful backend deletion (or if it's a temp file)
    const updatedFiles = files.filter((file) => file.name !== fileName)
    setFiles(updatedFiles)
    updateParentWithFiles(updatedFiles)
  }

  return (
    <div className={styles.Container}>
      <div
        className={`${styles.UploadArea} ${isDragging ? styles.Dragging : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={styles.IconWrapper}>
          <img src="/emptyFolder.svg" alt="Upload" />
        </div>
        <div className={styles.TextAndButtonWrapper}>
          <p className={styles.Text}>Drag and drop or</p>
          <div>
            <button type="button" className={buttonStyles['button-primary']} onClick={handleButtonClick}>
              Upload File
            </button>
          </div>
        </div>
      </div>

      <div className={styles.FileInfo}>
        <p className={styles.SupportedFiles}>
          Supported files: <span>.jpg, .png, .svg, .pdf, .doc, .docx</span>
        </p>
        <p className={styles.MaxSize}>
          Maximum file size: <span>100MB</span>
        </p>
      </div>

      {files.length > 0 && (
        <div className={styles.FileList}>
          {files.map((file) => (
            <div key={file.name} className={styles.FileItem}>
              <div className={styles.FileIcon} style={{ backgroundColor: getFileIconColor(file.type) }}>
                {getFileIcon(file.type)}
              </div>
              <div className={styles.FileDetails}>
                <p className={styles.FileName}>{file.name}</p>
                <p className={styles.FileMetadata}>
                  {file.date} | {formatFileSize(file.size)}
                </p>
              </div>
              <button type="button" className={styles.RemoveButton} onClick={() => handleRemoveFile(file.name)} aria-label="Remove file">
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".jpg,.jpeg,.png,.svg,.pdf,.doc,.docx"
        multiple
        className={styles.HiddenInput}
      />
    </div>
  )
}
